import React from "react";
import { useNavigate } from "react-router-dom";

import { faCircle } from "@fortawesome/pro-solid-svg-icons";

import { ButtonStandard, Icon, ModalBase, Text } from "@performyard/py-components";

import { useCompleteCompensationCycle } from "../../../../api/hooks/defs/compensation";

import { pluralize } from "../SharedComponents/utils";

import { useCycleBudgetOverview } from "../SharedComponents/useCycleBudgetOverview";

type CompleteCycleModalProps = {
	cycleId: string;
	closeModal: () => void;
};

const CompleteCycleModal: React.FC<CompleteCycleModalProps> = ({ cycleId, closeModal }) => {
	const navigate = useNavigate();
	const { mutate: completeCycle, isPending } = useCompleteCompensationCycle();
	const { submittedBudgetCount, notSubmittedBudgetCount } = useCycleBudgetOverview(cycleId);

	const allBudgetsApproved = !submittedBudgetCount && !notSubmittedBudgetCount;

	const cycleCompleteSubtext = allBudgetsApproved
		? "All compensation changes will be finalized."
		: "The following budgets still need attention. Are you sure you want to complete the cycle?";

	const handleCompleteCycle = () => {
		completeCycle(cycleId, {
			onSuccess: () => {
				closeModal();
				navigate("/compensation");
			},
		});
	};

	const handleViewNotSubmittedBudgets = () => {
		navigate(`/compensation/cycles/${cycleId}/budget-reviews?quickFilter=not-submitted`);
		closeModal();
	};

	const handleViewSubmittedBudgets = () => {
		navigate(`/compensation/cycles/${cycleId}/budget-reviews?quickFilter=submitted`);
		closeModal();
	};

	return (
		<ModalBase isOpen onClose={closeModal} dangerouslySetClassName="!overflow-hidden w-[416px] min-h-[164px]">
			<div className="flex flex-col items-center px-6 pb-6">
				<div className="flex w-full flex-col items-center text-center">
					<Text size="sub-heading" weight="semi-bold">
						Complete Cycle
					</Text>
					<div className="mx-auto max-w-[384px]">
						<Text size="paragraph-3" weight="regular">
							{cycleCompleteSubtext}
						</Text>
					</div>
				</div>
				{!allBudgetsApproved && (
					<div className="mt-4 flex max-w-[384px] flex-col gap-2">
						{notSubmittedBudgetCount > 0 && (
							<div className="flex flex-row items-center">
								<div className="flex flex-row items-center gap-2">
									<Icon icon={faCircle} size={12} color="neutral-3" />
									<div className="w-[190px]">
										<Text size="paragraph-3" weight="semi-bold">
											Not Submitted
										</Text>
									</div>
								</div>
								<div className="ml-4 w-[100px]">
									<Text size="paragraph-3" weight="regular">
										{notSubmittedBudgetCount}{" "}
										{pluralize(notSubmittedBudgetCount, "budget", "budgets")}
									</Text>
								</div>
								<div className="w-[84px]">
									<ButtonStandard
										variant="outline"
										color="primary"
										size="small"
										onClick={handleViewNotSubmittedBudgets}
									>
										View
									</ButtonStandard>
								</div>
							</div>
						)}
						{submittedBudgetCount > 0 && (
							<div className="flex flex-row items-center">
								<div className="flex flex-row items-center gap-2">
									<Icon icon={faCircle} size={12} color="yellow-2" />
									<div className="flex w-[190px] gap-1 whitespace-nowrap">
										<Text size="paragraph-3" weight="semi-bold">
											Submitted
										</Text>
										<Text size="paragraph-3" weight="regular">
											(needing approval)
										</Text>
									</div>
								</div>
								<div className="ml-4 w-[100px]">
									<Text size="paragraph-3" weight="regular">
										{submittedBudgetCount} {pluralize(submittedBudgetCount, "budget", "budgets")}
									</Text>
								</div>
								<div className="w-[84px]">
									<ButtonStandard
										variant="outline"
										color="primary"
										size="small"
										onClick={handleViewSubmittedBudgets}
									>
										View
									</ButtonStandard>
								</div>
							</div>
						)}
					</div>
				)}
				<div className="mt-6 flex flex-row items-center justify-center gap-2">
					<ButtonStandard variant="outline" color="neutral" size="large" onClick={closeModal}>
						Cancel
					</ButtonStandard>
					<ButtonStandard
						variant="solid"
						color="primary"
						size="large"
						onClick={handleCompleteCycle}
						disabled={isPending}
					>
						Yes, Complete Cycle
					</ButtonStandard>
				</div>
			</div>
		</ModalBase>
	);
};

export default CompleteCycleModal;
